const TAG = 'message-callout'
const TONES = new Set(['info', 'success', 'warning', 'danger'])

export function defineMessageCallout(): void {
  if (typeof window === 'undefined' || window.customElements.get(TAG)) return

  class MessageCalloutElement extends HTMLElement {
    static observedAttributes = ['title', 'tone']

    constructor() {
      super()
      this.attachShadow({ mode: 'open' })
    }

    connectedCallback() {
      const root = this.shadowRoot
      if (!root) return
      if (!root.firstChild) {
        root.innerHTML = `
          <style>
            :host { display: block; margin: 12px 0; --callout-accent: #3472a9; --callout-bg: #eef6fd; }
            :host([tone="success"]) { --callout-accent: #277a49; --callout-bg: #edf8f1; }
            :host([tone="warning"]) { --callout-accent: #9a6500; --callout-bg: #fff8e6; }
            :host([tone="danger"]) { --callout-accent: #b42318; --callout-bg: #fff1f0; }
            .box {
              overflow: hidden; border: 1px solid color-mix(in srgb, var(--callout-accent) 45%, white);
              border-left: 4px solid var(--callout-accent); border-radius: var(--rich-callout-radius, 6px);
              background: var(--rich-callout-bg, var(--callout-bg)); color: var(--rich-callout-color, #26323d);
            }
            .header { display: flex; align-items: center; gap: 9px; padding: 11px 14px 0; font-weight: 700; }
            .mark {
              display: inline-flex; width: 20px; height: 20px; align-items: center; justify-content: center;
              border-radius: 50%; background: var(--callout-accent); color: white; font-size: 12px;
            }
            .body { padding: 8px 14px 12px 43px; }
          </style>
          <aside class="box" role="note">
            <div class="header"><span class="mark" aria-hidden="true">i</span><span class="title"></span></div>
            <div class="body"><slot></slot></div>
          </aside>`
        root.querySelector('.header')?.addEventListener('dblclick', event => {
          if (!this.closest('[contenteditable="true"]')) return
          event.preventDefault()
          const title = window.prompt('Callout title:', this.getAttribute('title') ?? '')?.trim()
          if (!title) return
          this.setAttribute('title', title.slice(0, 160))
          this.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
        })
      }
      this.syncView()
    }

    attributeChangedCallback() {
      this.syncView()
    }

    private syncView() {
      const root = this.shadowRoot
      if (!root) return
      const tone = this.getAttribute('tone') ?? 'info'
      if (!TONES.has(tone)) this.setAttribute('tone', 'info')
      const title = root.querySelector<HTMLElement>('.title')
      const mark = root.querySelector<HTMLElement>('.mark')
      if (title) title.textContent = this.getAttribute('title') || 'Note'
      if (mark) mark.textContent = tone === 'success' ? 'OK' : tone === 'warning' || tone === 'danger' ? '!' : 'i'
    }
  }

  window.customElements.define(TAG, MessageCalloutElement)
}
